import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { KeyRound, LogIn, ShieldAlert, Search, RefreshCw } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';

const METHOD_LABEL = { microsoft_365: 'Microsoft 365', admin_key: 'Admin key', legacy_sso: 'Organisation SSO' };
const PAGE = 50;

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4 flex items-center gap-3" data-testid={testid}>
    <Icon className="h-5 w-5 text-navigate-gold" />
    <div>
      <div className="text-xl font-semibold text-white">{value ?? 0}</div>
      <div className="text-xs text-zinc-400">{label}</div>
    </div>
  </div>
);

const Row = ({ e }) => (
  <tr className="border-t border-zinc-800 text-sm" data-testid={`signin-row-${e.event_id}`}>
    <td className="py-3 pr-4 text-zinc-300 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
    <td className="py-3 pr-4 text-white">{e.name || '-'}<div className="text-xs text-zinc-500">{e.email || 'unknown'}</div></td>
    <td className="py-3 pr-4 text-zinc-300">{METHOD_LABEL[e.method] || e.method}</td>
    <td className="py-3 pr-4">
      <span className={`px-2 py-0.5 rounded-full text-xs border ${e.success ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/30' : 'bg-red-500/10 text-red-300 border-red-500/30'}`}>
        {e.success ? 'Signed in' : 'Failed'}
      </span>
      {e.reason && <div className="text-xs text-zinc-500 mt-1 max-w-xs">{e.reason}</div>}
    </td>
    <td className="py-3 pr-4 text-zinc-400">{e.role || '-'}</td>
    <td className="py-3 text-zinc-500 text-xs">{e.ip_address || '-'}</td>
  </tr>
);

export default function SignInActivityLog({ token }) {
  const [data, setData] = useState({ items: [], total: 0, summary: {} });
  const [method, setMethod] = useState('');
  const [outcome, setOutcome] = useState('');
  const [q, setQ] = useState('');
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { limit: PAGE, skip, ...(method && { method }), ...(outcome && { outcome }), ...(query && { q: query }) };
      const res = await axios.get(`${API_BASE}/api/admin/signin-activity`, { params, headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not load sign-in activity');
    } finally {
      setLoading(false);
    }
  }, [token, method, outcome, query, skip]);

  useEffect(() => { load(); }, [load]);

  const select = 'h-10 rounded-md bg-navigate-surface border border-zinc-800 text-sm text-white px-3';
  return (
    <div className="space-y-6" data-testid="signin-activity">
      <div>
        <h2 className="text-2xl font-heading font-bold text-white">Sign-In Activity</h2>
        <p className="text-sm text-zinc-400 mt-1">Every sign-in to the platform, with the method used and when it happened.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={LogIn} label="Microsoft 365 sign-ins" value={data.summary.microsoft_365} testid="signin-stat-m365" />
        <StatCard icon={KeyRound} label="Admin key sign-ins" value={data.summary.admin_key} testid="signin-stat-admin-key" />
        <StatCard icon={ShieldAlert} label="Failed attempts" value={data.summary.failed} testid="signin-stat-failed" />
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} placeholder="Search name or email" className="pl-9 bg-navigate-surface border-zinc-800" data-testid="signin-search" />
        </div>
        <select value={method} onChange={(e) => { setMethod(e.target.value); setSkip(0); }} className={select} data-testid="signin-filter-method">
          <option value="">All methods</option>
          <option value="microsoft_365">Microsoft 365</option>
          <option value="admin_key">Admin key</option>
          <option value="legacy_sso">Organisation SSO</option>
        </select>
        <select value={outcome} onChange={(e) => { setOutcome(e.target.value); setSkip(0); }} className={select} data-testid="signin-filter-outcome">
          <option value="">All outcomes</option>
          <option value="success">Signed in</option>
          <option value="failed">Failed</option>
        </select>
        <Button variant="outline" onClick={load} className="border-zinc-700" data-testid="signin-refresh"><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
      </div>
      <div className="bg-navigate-surface border border-zinc-800 rounded-xl p-4 overflow-x-auto">
        <table className="w-full text-left" data-testid="signin-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-zinc-500">
              <th className="pb-2 pr-4">When</th><th className="pb-2 pr-4">Who</th><th className="pb-2 pr-4">Method</th>
              <th className="pb-2 pr-4">Outcome</th><th className="pb-2 pr-4">Role</th><th className="pb-2">IP address</th>
            </tr>
          </thead>
          <tbody>{data.items.map((e) => <Row key={e.event_id} e={e} />)}</tbody>
        </table>
        {error && <p className="text-sm text-red-400 py-4 text-center" data-testid="signin-error">{typeof error === 'string' ? error : 'Could not load sign-in activity'}</p>}
        {!loading && !error && data.items.length === 0 && <p className="text-sm text-zinc-500 py-6 text-center" data-testid="signin-empty">No sign-ins recorded yet.</p>}
      </div>
      <div className="flex items-center justify-between text-sm text-zinc-400">
        <span data-testid="signin-total">{data.total} records</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))} className="border-zinc-700" data-testid="signin-prev">Previous</Button>
          <Button variant="outline" size="sm" disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)} className="border-zinc-700" data-testid="signin-next">Next</Button>
        </div>
      </div>
    </div>
  );
}
